import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Flex, Stack, Surface } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createBusinessesApi } from "../api";
import { formatLabel } from "../labels";
import type { BusinessTypeDetailResponse, BusinessTypeDto } from "../types";

export function BusinessTypesDirectoryPage() {
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Business Center", to: "/app/businesses" },
    { label: "Business Types" },
  ]);

  const client = useApiClient();
  const api = useMemo(() => createBusinessesApi(client), [client]);
  const navigate = useNavigate();
  const [types, setTypes] = useState<BusinessTypeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.listTypes();
      setTypes(response.business_types);
    } catch (err) {
      setError(apiErrorMessage(err));
      setTypes([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Business Types"
        description="Business Type defines governed behavior. Marketplace Category is grouping only. Sports Facility is a Business Type, not a capability."
        actions={
          <Button variant="secondary" onClick={() => void navigate("/app/businesses")}>
            Back to Businesses
          </Button>
        }
      />
      {error ? (
        <Alert tone="danger" title="Unable to load Business Types">
          {error}
        </Alert>
      ) : null}
      {loading ? <LoadingState message="Loading Business Types…" /> : null}
      {!loading && !error && types.length === 0 ? (
        <EmptyState
          title="No Business Types"
          description="Type definitions are reference data. Global type changes route to System approval."
          reason="none"
        />
      ) : null}
      {!loading && types.length > 0 ? (
        <Table>
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Slug</Th>
              <Th>Status</Th>
              <Th>Marketplace Category ID</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {types.map((type) => (
              <Tr key={type.id}>
                <Td>
                  <Text role="bodyStrong" as="div">
                    {type.name}
                  </Text>
                </Td>
                <Td>{type.slug}</Td>
                <Td>
                  <StatusChip>{formatLabel(type.status)}</StatusChip>
                </Td>
                <Td>{type.marketplace_category_id}</Td>
                <Td>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() =>
                      void navigate(`/app/businesses/types/${type.id}`)
                    }
                  >
                    Open
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : null}
    </Stack>
  );
}

export function BusinessTypeDetailPage() {
  const { businessTypeId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createBusinessesApi(client), [client]);
  const navigate = useNavigate();
  const [detail, setDetail] = useState<BusinessTypeDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Business Center", to: "/app/businesses" },
    { label: "Business Types", to: "/app/businesses/types" },
    { label: detail?.business_type.name ?? "Business Type" },
  ]);

  const load = useCallback(async () => {
    if (!businessTypeId) return;
    setLoading(true);
    setError(null);
    try {
      setDetail(await api.getType(businessTypeId));
    } catch (err) {
      setError(apiErrorMessage(err));
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [api, businessTypeId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading Business Type…" />;
  if (error || !detail) {
    return (
      <EmptyState
        title="Business Type unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const { business_type, capabilities } = detail;

  return (
    <Stack gap={24}>
      <SectionHeader
        title={business_type.name}
        description={business_type.slug}
        actions={
          <Flex gap={8}>
            <Button
              variant="secondary"
              onClick={() => void navigate("/app/businesses/types")}
            >
              All types
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                void navigate(
                  `/app/businesses?business_type_id=${business_type.id}`,
                )
              }
            >
              Businesses of this type
            </Button>
          </Flex>
        }
      />
      <Card
        header={
          <Text role="title" as="h3">
            Type definition
          </Text>
        }
      >
        <Stack gap={8}>
          <Text role="body" as="p">
            Status: {formatLabel(business_type.status)}
          </Text>
          <Text role="body" as="p">
            Marketplace Category ID: {business_type.marketplace_category_id}
          </Text>
          <Text role="body" as="p">
            Description: {business_type.description ?? "—"}
          </Text>
          <Text role="caption" color="muted" as="p">
            Global type or category governance is Super Admin / System — not
            edited here.
          </Text>
        </Stack>
      </Card>
      <Surface>
        <Stack gap={16}>
          <Text role="h2" as="h2">
            Type capabilities
          </Text>
          {capabilities.length === 0 ? (
            <EmptyState
              title="No type capabilities"
              description="Type-wide capability definitions are empty for this Business Type."
              reason="none"
            />
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>Capability type</Th>
                  <Th>Status</Th>
                  <Th>Effective from</Th>
                  <Th>Effective to</Th>
                </Tr>
              </Thead>
              <Tbody>
                {capabilities.map((row) => (
                  <Tr key={row.id}>
                    <Td>{formatLabel(row.capability_type)}</Td>
                    <Td>{formatLabel(row.status)}</Td>
                    <Td>{row.effective_from}</Td>
                    <Td>{row.effective_to ?? "—"}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Stack>
      </Surface>
    </Stack>
  );
}
